<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import { useInvoiceCancellation } from '@/composables/shipment/useInvoiceCancellation'
import CancelInvoiceDialog from '@/components/shipment/CancelInvoiceDialog.vue'
import { useUserRoles } from '@/composables/useUserRoles'
import { useSnackbar } from '@/composables/useSnackbar'
import { formatCurrency } from '@/utils/currencyUtils'

definePage({
  meta: {
    action: 'read',
    subject: 'Warehouse',
    navActiveLink: 'shipment-warehouse-invoices',
  },
})

// Invoice list — central place for finance to see what's paid vs unpaid
// without drilling into each container's client groups. Filters:
//   - status (all / paid / unpaid / draft)
//   - container (optional)
//   - free-text search across quote_number / external_invoice_number /
//     customer name / phone / client_id
//
// Status mapping (UI ← backend ShippingTransaction.status):
//   paid   ← 'paid'
//   unpaid ← 'quoted' or 'invoiced'  (generated, not yet marked paid)
//   draft  ← 'draft'
//
// API: /api/shipment/warehouse/invoices

const { t } = useI18n()
const router = useRouter()

const rows = ref([])
const totals = ref({ all: 0, paid: 0, unpaid: 0, draft: 0 })
const loading = ref(false)

const statusFilter = ref('all') // 'all' | 'paid' | 'unpaid' | 'draft'
const search = ref('')
const containerId = ref(null)
const containers = ref([])  // list of {id, reference_code}
const currentPage = ref(1)
const perPage = ref(25)
const totalRows = ref(0)
const lastPage = ref(1)

const STATUS_OPTIONS = computed(() => [
  { value: 'all',    label: t('shipment.invoice_filter_all'),    color: 'default' },
  { value: 'unpaid', label: t('shipment.invoice_filter_unpaid'), color: 'warning' },
  { value: 'paid',   label: t('shipment.invoice_filter_paid'),   color: 'success' },
  { value: 'draft',  label: t('shipment.invoice_filter_draft'),  color: 'default' },
])

const fetchContainers = async () => {
  try {
    const { data } = await axios.get('shipment/container-arrivals', { params: { per_page: 200 } })
    const list = data?.data ?? data
    containers.value = (Array.isArray(list) ? list : []).map(c => ({
      id: c.id,
      reference_code: c.reference_code || `#${c.id}`,
    }))
  } catch (e) {
    console.error('fetchContainers', e)
  }
}

const fetchRows = async () => {
  loading.value = true
  try {
    const params = {
      page: currentPage.value,
      per_page: perPage.value,
    }
    if (statusFilter.value !== 'all') params.status = statusFilter.value
    if (search.value?.trim()) params.q = search.value.trim()
    if (containerId.value) params.container_arrival_id = containerId.value

    const { data } = await axios.get('shipment/warehouse/invoices', { params })
    rows.value     = data.data
    totals.value   = data.totals
    totalRows.value = data.meta?.total ?? 0
    lastPage.value  = data.meta?.last_page ?? 1
  } catch (e) {
    console.error('fetchRows', e)
  } finally {
    loading.value = false
  }
}

const formatMoney = (amount, currency) => {
  if (amount === null || amount === undefined) return '—'
  return formatCurrency(amount, currency || 'MGA')
}

const formatDate = iso => {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso))
  } catch { return iso }
}

const statusChip = (row) => {
  if (row.paid) return { color: 'success', icon: 'tabler-check', label: t('shipment.invoice_status_paid') }
  if (row.status === 'draft') return { color: 'default', icon: 'tabler-pencil', label: t('shipment.invoice_status_draft') }
  return { color: 'warning', icon: 'tabler-clock', label: t('shipment.invoice_status_unpaid') }
}

const openInvoice = id => router.push({ name: 'shipment-warehouse-invoices-view', params: { invoiceId: id } })

// Cancel-invoice action — superadmin only. The composable owns state
// + the actual DELETE call; we just open it and refresh on success.
const invoiceCancellation = useInvoiceCancellation({
  onSuccess: async () => {
    await fetchRows()
  },
})

const requestCancel = (row, e) => {
  // Stop the click from bubbling into the row's openInvoice handler.
  e?.stopPropagation?.()
  invoiceCancellation.request({
    invoiceId: row.id,
    groupId: row.group?.id,
    groupRef: row.customer?.name
      ? `${row.customer.name} (${row.quote_number})`
      : row.quote_number,
    externalNumber: row.external_invoice_number,
  })
}

// Mark-as-paid flow — collects the external invoice number from the
// money-role operator, then PUTs to the workflow-status endpoint
// (same as the per-group dialog inside container detail). Backend
// re-validates (money-role only, requires external invoice number).
const { isMoney, isSuperadmin: roleIsSuperadmin, isAdmin } = useUserRoles()
const { showSnackbar } = useSnackbar()
const canMarkPaid = computed(() => isMoney.value || roleIsSuperadmin.value || isAdmin.value)

const paidDialog = ref(false)
const paidContext = ref(null)         // { invoiceId, groupId, groupRef, currentExternal? }
const paidExternalNumber = ref('')
const paidLoading = ref(false)

const requestMarkPaid = (row, e) => {
  e?.stopPropagation?.()
  if (!row.group?.id) {
    showSnackbar({ color: 'error', text: 'No group attached to this invoice' })
    return
  }
  paidContext.value = {
    invoiceId: row.id,
    groupId:   row.group.id,
    groupRef:  row.customer?.name
      ? `${row.customer.name} (${row.quote_number})`
      : row.quote_number,
  }
  paidExternalNumber.value = row.external_invoice_number || ''
  paidDialog.value = true
}

const closePaidDialog = () => {
  paidDialog.value = false
  paidContext.value = null
  paidExternalNumber.value = ''
}

const confirmMarkPaid = async () => {
  if (!paidContext.value) return
  const num = paidExternalNumber.value?.trim()
  if (!num) {
    showSnackbar({
      color: 'warning',
      text: t('shipment.payer_requires_external_invoice_number'),
    })
    return
  }
  paidLoading.value = true
  try {
    await axios.put(
      `shipment/warehouse/groups/${paidContext.value.groupId}/workflow-status`,
      { workflow_status: 'PAYER', external_invoice_number: num },
    )
    showSnackbar({
      color: 'success',
      text: t('shipment.workflow_updated') || 'Statut mis à jour',
    })
    closePaidDialog()
    await fetchRows()
  } catch (e) {
    console.error('markPaid', e)
    showSnackbar({
      color: 'error',
      text: e.response?.data?.message || t('messages.something_went_wrong'),
    })
  } finally {
    paidLoading.value = false
  }
}

watch([statusFilter, containerId], () => {
  currentPage.value = 1
  fetchRows()
})
let searchTimer = null
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    currentPage.value = 1
    fetchRows()
  }, 300)
})
watch(currentPage, fetchRows)

onMounted(async () => {
  await Promise.all([fetchContainers(), fetchRows()])
})
</script>

<template>
  <div>
    <VCard>
      <VCardTitle class="d-flex align-center flex-wrap py-4 gap-3">
        <div class="d-flex align-center gap-2">
          <VIcon icon="tabler-file-invoice" />
          <h3 class="text-h4 mb-0">
            {{ t('shipment.invoices') }}
          </h3>
        </div>

        <VSpacer />

        <VBtn
          variant="tonal"
          color="primary"
          prepend-icon="tabler-refresh"
          :loading="loading"
          @click="fetchRows"
        >
          {{ t('actions.refresh') }}
        </VBtn>
      </VCardTitle>

      <VCardText class="pt-0">
        <!-- Status chips with totals -->
        <div class="d-flex align-center flex-wrap gap-2 mb-4">
          <VChip
            v-for="opt in STATUS_OPTIONS"
            :key="opt.value"
            :color="statusFilter === opt.value ? opt.color === 'default' ? 'primary' : opt.color : opt.color"
            :variant="statusFilter === opt.value ? 'flat' : 'outlined'"
            size="small"
            class="font-weight-bold"
            @click="statusFilter = opt.value"
          >
            {{ opt.label }}
            <VChip
              v-if="opt.value !== 'all'"
              :color="opt.color === 'default' ? 'secondary' : opt.color"
              size="x-small"
              variant="elevated"
              class="ms-2"
            >
              {{ totals[opt.value] ?? 0 }}
            </VChip>
            <VChip
              v-else
              color="primary"
              size="x-small"
              variant="elevated"
              class="ms-2"
            >
              {{ totals.all ?? 0 }}
            </VChip>
          </VChip>
        </div>

        <!-- Search + container filter -->
        <VRow class="mb-3" dense>
          <VCol cols="12" md="6">
            <VTextField
              v-model="search"
              :placeholder="t('shipment.invoice_search_placeholder')"
              density="compact"
              variant="outlined"
              prepend-inner-icon="tabler-search"
              hide-details
              clearable
            />
          </VCol>
          <VCol cols="12" md="6">
            <VAutocomplete
              v-model="containerId"
              :items="containers"
              :label="t('shipment.container')"
              item-title="reference_code"
              item-value="id"
              density="compact"
              variant="outlined"
              hide-details
              clearable
            />
          </VCol>
        </VRow>

        <VProgressLinear v-if="loading" indeterminate color="primary" class="mb-3" />

        <VAlert
          v-if="!loading && rows.length === 0"
          type="info"
          variant="tonal"
        >
          {{ t('shipment.invoice_empty') }}
        </VAlert>

        <!-- Table -->
        <VTable v-else density="compact" hover>
          <thead>
            <tr>
              <th>{{ t('shipment.invoice_number') }}</th>
              <th>{{ t('shipment.external_invoice_number_label') }}</th>
              <th>{{ t('shipment.customer') }}</th>
              <th>{{ t('shipment.container') }}</th>
              <th class="text-end">{{ t('shipment.amount') }}</th>
              <th>{{ t('shipment.status') }}</th>
              <th>{{ t('shipment.created_at') }}</th>
              <th class="text-end">{{ t('actions.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.id"
              style="cursor: pointer"
              @click="openInvoice(row.id)"
            >
              <td>
                <code class="text-body-2">{{ row.quote_number }}</code>
              </td>
              <td>
                <span v-if="row.external_invoice_number" class="font-weight-medium">
                  {{ row.external_invoice_number }}
                </span>
                <span v-else class="text-caption text-medium-emphasis">—</span>
              </td>
              <td>
                <div v-if="row.customer">
                  <CustomerLink
                    :customer="row.customer"
                    :show-client-id="false"
                  />
                  <div class="text-caption text-medium-emphasis">
                    {{ row.customer.client_id || row.customer.phone || '—' }}
                  </div>
                </div>
                <span v-else class="text-caption text-medium-emphasis">—</span>
              </td>
              <td>
                <VChip
                  v-if="row.container"
                  size="x-small"
                  variant="tonal"
                  color="info"
                  :to="{ name: 'history-containers-detail', params: { containerId: row.container.id } }"
                  link
                  @click.stop
                >
                  {{ row.container.reference_code }}
                </VChip>
              </td>
              <td class="text-end font-weight-medium">
                {{ formatMoney(row.total_amount, row.currency) }}
              </td>
              <td>
                <VChip
                  :color="statusChip(row).color"
                  size="x-small"
                  variant="tonal"
                >
                  <VIcon :icon="statusChip(row).icon" start size="14" />
                  {{ statusChip(row).label }}
                </VChip>
                <div
                  v-if="row.group?.workflow_status"
                  class="text-caption text-medium-emphasis mt-1"
                >
                  {{ row.group.workflow_status }}
                </div>
              </td>
              <td>{{ formatDate(row.created_at) }}</td>
              <td class="text-end">
                <VBtn
                  v-if="!row.paid && canMarkPaid && row.group?.id"
                  size="x-small"
                  variant="text"
                  color="success"
                  icon="tabler-cash-banknote"
                  :title="t('shipment.mark_as_paid_dialog_title')"
                  @click.stop="requestMarkPaid(row, $event)"
                />
                <VBtn
                  v-if="invoiceCancellation.isSuperadmin"
                  size="x-small"
                  variant="text"
                  color="error"
                  icon="tabler-receipt-off"
                  :title="t('shipment.cancel_invoice')"
                  @click.stop="requestCancel(row, $event)"
                />
                <VBtn
                  size="x-small"
                  variant="text"
                  icon="tabler-arrow-right"
                  @click.stop="openInvoice(row.id)"
                />
              </td>
            </tr>
          </tbody>
        </VTable>

        <!-- Pagination -->
        <div v-if="totalRows > perPage" class="d-flex justify-center mt-4">
          <VPagination
            v-model="currentPage"
            :length="lastPage"
            :total-visible="7"
          />
        </div>
      </VCardText>
    </VCard>

    <CancelInvoiceDialog :ctl="invoiceCancellation" />

    <!-- Mark-as-paid dialog (money / admin / superadmin) -->
    <VDialog v-model="paidDialog" max-width="540" persistent>
      <VCard v-if="paidContext">
        <VCardTitle class="d-flex align-center">
          <VIcon icon="tabler-cash-banknote" color="success" class="me-2" />
          {{ t('shipment.mark_as_paid_dialog_title') }}
        </VCardTitle>
        <VCardText>
          <p class="mb-3">
            {{ t('shipment.mark_as_paid_dialog_body') }}
          </p>
          <p class="text-body-2 text-medium-emphasis mb-3">
            <strong>{{ paidContext.groupRef }}</strong>
          </p>
          <VTextField
            v-model="paidExternalNumber"
            :label="t('shipment.external_invoice_number_label')"
            :hint="t('shipment.external_invoice_number_hint')"
            persistent-hint
            variant="outlined"
            density="compact"
            autofocus
            @keyup.enter="confirmMarkPaid"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" :disabled="paidLoading" @click="closePaidDialog">
            {{ t('common.cancel') }}
          </VBtn>
          <VBtn
            color="success"
            variant="elevated"
            prepend-icon="tabler-cash-banknote"
            :loading="paidLoading"
            :disabled="!paidExternalNumber?.trim()"
            @click="confirmMarkPaid"
          >
            {{ t('shipment.mark_as_paid_dialog_title') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
